// ─────────────────────────────────────────────────────────────────────────────
// GUEST SPACES v0 (Ticket 9 deliverable 3) — a per-guest page assembled from
// what exists:
//
//   episodes   the candidate cards carrying the guest's slug in people[]
//   profile    a Reflections-built subject-profile block, where one exists
//              (/guests/<slug>.json on the show origin — a bare static GET)
//   follow     an atproto follow affordance when the guest is on-network
//              (guest.did present); otherwise a flag-dark email-capture
//              fallback (VITE_GUEST_FOLLOW_EMAIL_CAPTURE, default OFF)
//
// CONSENT (invariant): guest pages contain only consented material. The
// publish-time rule is that static profile files hold only consentClass
// 'public' blocks; `consentFilterBlocks` re-enforces that at render as
// defense-in-depth (for the day profiles come from a live service instead of
// a static file) and is tested against a non-public fixture.
// ─────────────────────────────────────────────────────────────────────────────

import {assertBareShowFetch} from './showFence'
import type {Card} from './candidateSet'

export interface GuestEntry {
  slug: string
  name: string
  initials: string
  note?: string
  episodeIds: string[]
  did?: string | null
  profile?: boolean
}

export interface GuestProfileBlock {
  kind: string
  consentClass?: string
  title?: string
  text?: string
}

export interface GuestProfile {
  slug: string
  name: string
  source?: string
  updatedAt?: string
  blocks: GuestProfileBlock[]
}

export interface GuestSpaceModel {
  guest: GuestEntry
  episodes: Card[]
  profile: GuestProfile | null
  follow:
    | {kind: 'atproto'; did: string}
    | {kind: 'email-capture'}
    | {kind: 'none'}
}

/** The email-capture fallback is FLAG-DARK by default (Ticket 9 deliverable 3). */
export function emailCaptureEnabled(): boolean {
  return import.meta.env?.VITE_GUEST_FOLLOW_EMAIL_CAPTURE === 'true'
}

/** Episodes for a guest = candidate cards filtered by people[]. */
export function episodesFor(cards: Card[], slug: string): Card[] {
  return cards.filter(c => Array.isArray(c.people) && c.people.includes(slug))
}

/**
 * THE CONSENT FILTER: only 'public' blocks may render on a public guest page.
 * Anything else — self-only, this-agent, owner-shared, org, or an ABSENT
 * class — is dropped. Fail closed: unlabeled material is not consented
 * material.
 */
export function consentFilterBlocks(blocks: GuestProfileBlock[] | undefined | null): GuestProfileBlock[] {
  return (blocks ?? []).filter(b => b && b.consentClass === 'public')
}

/** Load the guest registry (bare same-origin GET, fence-checked). */
export async function loadGuestRegistry(url = '/guests/registry.json'): Promise<GuestEntry[]> {
  const init: RequestInit = {method: 'GET', credentials: 'omit'}
  assertBareShowFetch(url, init)
  const res = await fetch(url, init)
  if (!res.ok) throw new Error(`guest registry unavailable (${res.status})`)
  const doc = await res.json()
  return Array.isArray(doc?.guests) ? doc.guests : []
}

/** Load a guest's Reflections profile, or null when none exists (clean degrade). */
export async function loadGuestProfile(slug: string): Promise<GuestProfile | null> {
  const url = `/guests/${encodeURIComponent(slug)}.json`
  const init: RequestInit = {method: 'GET', credentials: 'omit'}
  assertBareShowFetch(url, init)
  const res = await fetch(url, init)
  if (!res.ok) return null
  let doc: any
  try {
    doc = await res.json()
  } catch {
    return null
  }
  if (!doc || !Array.isArray(doc.blocks)) return null
  return {
    slug: String(doc.slug ?? slug),
    name: String(doc.name ?? slug),
    source: typeof doc.source === 'string' ? doc.source : undefined,
    updatedAt: typeof doc.updatedAt === 'string' ? doc.updatedAt : undefined,
    blocks: consentFilterBlocks(doc.blocks),
  }
}

/** Assemble the guest-space model. Pure over its inputs (testable offline). */
export function assembleGuestSpace(
  guest: GuestEntry,
  cards: Card[],
  profile: GuestProfile | null,
  opts: {emailCapture?: boolean} = {},
): GuestSpaceModel {
  const follow: GuestSpaceModel['follow'] = guest.did
    ? {kind: 'atproto', did: guest.did}
    : opts.emailCapture
      ? {kind: 'email-capture'}
      : {kind: 'none'}
  return {
    guest,
    episodes: episodesFor(cards, guest.slug),
    profile: profile ? {...profile, blocks: consentFilterBlocks(profile.blocks)} : null,
    follow,
  }
}
